import { autoinject } from 'aurelia-dependency-injection';
import { bindable, bindingMode } from 'aurelia-framework';
import { HttpClient } from 'aurelia-fetch-client';
import { Router } from 'aurelia-router';
import { LicenseInfo } from '../../services/license-info';
import { OrganizationInfo, CrmSolutionInfo, FeatureInfo } from '../../services/organization-info';
import { ISolutionFeatureMapping } from '../solution-featurecode-mapping';
import { LicenseStatusCode } from '../license-status';
import { FeatureInfoStore } from '../../services/feature-info-store';

@autoinject
export class SolutionStatus {

    constructor(private http: HttpClient, private router: Router, private featureInfoStore: FeatureInfoStore, private orgInfo: OrganizationInfo, private licenseInfo: LicenseInfo) {
        http.configure(x => {
            x.useStandardConfiguration()
        });
    }

    solutions: CrmSolutionInfo[] = [];
    isLoaded: Boolean = false;
    @bindable organizationid: string = "";
    @bindable customerid: string = "";
    @bindable usefmnl: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.twoWay }) invalidLicensesCount: number = 0;
    @bindable({ defaultBindingMode: bindingMode.twoWay }) invalidLicenses: FeatureInfo[] = [];


    bind() {
        console.log(`bind solution-status pane for organizationid=`, this.organizationid);

        this.init();
    }

    
    private async init() {
        console.log("Retrieve licensed solution info");
        console.log('usefmnl', this.usefmnl);
        try {
            // Start retrieving crmandmore solutions from CRM
            const retrieveLicensedSolutions = this.orgInfo.retrieveLicensedSolutions();

            // Start retrieving solution feature code mapping
            const retrieveSolutionMapping = this.featureInfoStore.getFeatureSolutionMapping();

            const licensedSolutions: CrmSolutionInfo[] = await retrieveLicensedSolutions;

            const solutionMapping: ISolutionFeatureMapping = await retrieveSolutionMapping;

            // Tell bullet-spinner solutions are loaded
            this.isLoaded = true;

            const consumptionBasedSupported = this.checkIsConsumptionBasedSupported(licensedSolutions);
            console.log(`ConsumptionBasedSupported: ${consumptionBasedSupported}`);

            // Validate for each solution its license
            for (const solution of licensedSolutions) {
                let features = solutionMapping[solution.uniquename];
                if (!features || features.length === 0) {
                    continue;
                }

                console.log(`${solution.uniquename}, v${solution.version}`);

                // CRM Essentials, don't show subfeatures for version lower than 9.2.x 
                if (this.isPreviousVersionOfCrmEssentials(solution)) {
                    features = solutionMapping["CRMandMore_CRMEssentials_Prev"];
                }

                // Init features with empty array
                solution.features = [];

                // Add and validate the features
                Object.keys(features).forEach(featureName => {

                    const featureCode = features[featureName];

                    // add to list
                    const feature: FeatureInfo = {
                        featureCode: featureCode,
                        featureName: featureName,
                        licenseStatus: ""
                    };

                    // push feature into solution object
                    solution.features.push(feature);

                    // validate
                    this.validateFeature(feature, solution.version, consumptionBasedSupported);
                });

                this.solutions.push(solution);
            }
        }
        catch (e) {
            console.log(`error init solution-status component`, e);
            this.router
                .navigateToRoute('error', { errorMessage: `Error retrieving CRM solution info: ${e}` });
        }
    }

    /**
    * Verifies license status of a feature, calling the licenseserver api.
    */
    async validateFeature(feature: FeatureInfo, solutionVersion: string, consumptionBasedSupported: boolean) {

        console.log(`validate feature: `, feature.featureCode);

        // Tell client we are busy
        feature.validating = true;

        const enforceConsumptionBased = this.shouldEnforceConsumptionBased(feature, solutionVersion);

        const prefix = this.getPrefix();
        console.log(`using prefix: `, prefix);

        // Retrieve license and when fails, catch with status NoLicense
        this.orgInfo.retrieveLicense(feature.featureCode, consumptionBasedSupported, prefix)
            .then(license => {

                const isTrial = license[`${prefix}_istrial`];
                if (isTrial) {
                    this.orgInfo.determineRemainingDaysForLicense(feature, license[`${prefix}_expirationdate`]);
                }

                // Check requirement of consumptionbased
                if (enforceConsumptionBased) {

                    // Look if the current license is marked as consumptionbased
                    if (license[`${prefix}_isconsumptionbased`] !== true) {
                        console.log('Feature requires consumptionbased license for this solution version but is currently not. Solutionversion', solutionVersion);
                        feature.licenseStatus = LicenseStatusCode.LicenseInvalid;
                        feature.validating = false;
                        this.invalidLicenses.push(feature);
                        return;
                    }
                }

                this.licenseInfo.validateLicenseForFeature(feature, license, prefix, this.organizationid)
                    .then((licenseStatus) => {
                        const validTrial = licenseStatus === "Valid" && isTrial;
                        // when not valid (or valid trial), add to invalid-licenses list
                        if (licenseStatus !== "Valid" || validTrial) {
                            this.invalidLicenses.push(feature);
                        }
                    })
                    .catch((error) => {
                        // Something wrong, navigate to error page
                        return error.text().then((errorBody: string) => {
                            console.log('Error validating license: ', error);
                            this.router.navigateToRoute('error', { errorMessage: `Error validating license for feature ${feature.featureCode}, detail: ${error.statusText}` });
                        });
                    })
                    .then(() => {
                        // Release busy flag for this feature
                        feature['validating'] = false;
                    });

            })
            .catch(error => {
                console.log(`error getting license`, error);

                feature['validating'] = false;

                feature.licenseStatus = LicenseStatusCode.NoLicense;

                this.invalidLicenses.push(feature);
            });
    }

    private getPrefix() {
        if (this.usefmnl) {
            return "fmnl";
        }
        return "cnm";
    }

    private shouldEnforceConsumptionBased(feature: FeatureInfo, solutionVersion: string) {

        if (feature.featureCode !== 104) { // Address Connect
            return false;
        }

        // Current feature is Address Connect, look at the version if this solution version requires consumption based license
        const versionParts = this.getVersionParts(solutionVersion);

        // Address Assist 9.6.x (or higher) requires consumption based license
        if (versionParts[0] > 9 || (versionParts[0] == 9 && versionParts[1] >= 6)) {
            return true;
        }

        return false;
    }

    private checkIsConsumptionBasedSupported(solutions: CrmSolutionInfo[]) {

        for (const solutionInfo of solutions) {

            // App Foundation solution fully supports consumption based
            if (solutionInfo.uniquename === "Fellowmind_AppFoundation") {
                return true;
            }

            // Look for Licensing solution, skip others
            if (solutionInfo.uniquename !== "CRMandMore_Licensing") {
                continue;
            }

            // Read the version of the Licensing solution
            // Version 9.2.5.x and higher supports consumption based licensing
            const versionParts = this.getVersionParts(solutionInfo.version);

            if (versionParts[0] > 9
                || (versionParts[0] == 9 && versionParts[1] > 2)
                || (versionParts[0] == 9 && versionParts[1] == 2 && versionParts[2] >= 5)) {
                return true;
            }
        }

        return false;
    }

    private isPreviousVersionOfCrmEssentials(solution: CrmSolutionInfo): boolean {

        if (solution.uniquename !== "CRMandMore_CRMEssentials") {
            return false;
        }

        // Example incoming numbers: 
        //      8.3.3.0
        //      8.4.0.99
        //      9.2.0.0
        //     10.2.1.0

        // Extract version number
        const versionParts = this.getVersionParts(solution.version);

        // When versionnumber lower than 9.2.x this is a previous CRM Essentials version
        if (versionParts[0] < 9 || (versionParts[0] === 9 && versionParts[1] < 2)) {
            return true;
        }

        // Version is 9.2 or higher
        return false;
    }

    private getVersionParts(solutionVersion: string) {
        const versionParts = solutionVersion.split(".").map(function (item) {
            return parseInt(item, 10);
        });
        return versionParts;
    }
}


// WEBPACK FOOTER //
// ./ClientApp/app/components/licensing-overview/solution-status.ts